import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlayCircle, GraduationCap, UserRound, Building2, ArrowLeft, Lock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ROLES = [
  { role: "student", label: "Student", desc: "Explore institutes, teachers & home tuition", Icon: GraduationCap, path: "/student", bg: "#93C5FD" },
  { role: "teacher", label: "Teacher", desc: "Manage your tutor profile & inquiries", Icon: UserRound, path: "/teacher", bg: "#C4B5FD" },
  { role: "institute", label: "Institute", desc: "Coaching / school dashboard & listings", Icon: Building2, path: "/institute", bg: "#A3E635" },
];

export const DemoLogin = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("creds");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const verify = async (e) => {
    e.preventDefault(); setBusy("verify");
    try { await api.post("/auth/demo/verify", form); setStep("roles"); toast.success("Demo access granted — pick a profile"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const enter = async (r) => {
    setBusy(r.role);
    try { const { data } = await api.post(`/auth/demo/enter/${r.role}`); sessionStorage.removeItem("active-listing"); login(null, data.user); navigate(r.path); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  if (!open) return (
    <button type="button" data-testid="demo-login-btn" onClick={() => setOpen(true)} className="nb-btn w-full mt-3 justify-center bg-white">
      <PlayCircle className="w-4 h-4" /> Demo Login
    </button>
  );
  return (
    <div data-testid="demo-login-panel" className="mt-4 border-2 border-dashed border-[#171B33] rounded-2xl p-4 bg-[#FAFAF5]">
      <div className="flex items-center justify-between">
        <p className="font-display font-extrabold flex items-center gap-2"><PlayCircle className="w-4 h-4" /> Demo Login</p>
        <button type="button" data-testid="demo-login-close" onClick={() => { setOpen(false); setStep("creds"); }} className="text-xs font-bold text-slate-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</button>
      </div>
      {step === "creds" ? (
        <form onSubmit={verify} className="mt-3 space-y-2.5">
          <input data-testid="demo-email-input" type="email" required placeholder="Demo email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="nb-input" />
          <input data-testid="demo-password-input" type="password" required placeholder="Demo password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="nb-input" />
          <button data-testid="demo-login-submit" disabled={busy === "verify"} className="nb-btn nb-btn-lime w-full justify-center"><Lock className="w-4 h-4" /> {busy === "verify" ? "Checking…" : "Continue"}</button>
          <p className="text-[11px] text-slate-500">Shared demo credentials. Demo data is sample-only and resets periodically.</p>
        </form>
      ) : (
        <div className="mt-3 grid gap-2" data-testid="demo-role-picker">
          <p className="text-xs text-slate-500">Choose which profile to open:</p>
          {ROLES.map((r) => (
            <button key={r.role} type="button" data-testid={`demo-role-${r.role}`} disabled={!!busy} onClick={() => enter(r)}
              className="flex items-center gap-3 text-left border-2 border-[#171B33] rounded-xl p-3 bg-white shadow-[3px_3px_0_#171B33] hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#171B33] transition-[transform,box-shadow] duration-150 disabled:opacity-60">
              <span className="w-10 h-10 rounded-lg border-2 border-[#171B33] flex items-center justify-center shrink-0" style={{ background: r.bg }}><r.Icon className="w-5 h-5" /></span>
              <span><span className="block font-extrabold">{busy === r.role ? "Opening…" : r.label}</span><span className="block text-xs text-slate-500">{r.desc}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
